import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Save, RefreshCw, AlertTriangle, Unplug } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { xeroBankFeedAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

export const isXeroNotConnectedError = (err) =>
  err?.response?.status === 409 && err.response?.data?.status === 'xero_not_connected';

/**
 * Creates a Xero bank-account mapping: a Xero Account (AccountID, from the
 * tenant connected to the selected entity) -> an internal BR OS bank
 * account. The one-active-mapping-per-internal-account guard is enforced
 * server-side at submit time; this UI surfaces the account's existing
 * mapping/feed conflict up front so the operator sees it before submitting.
 *
 * Display and ranking-boost only -- this mapping only controls what
 * services/xero_accounting_reconciliation.py's daily read-only pull scans,
 * never a match or auto-confirm path.
 */
export function XeroBankFeedConfigureModal({ open, onOpenChange, onSaved }) {
  const [entities, setEntities] = useState([]);
  const [entityId, setEntityId] = useState('');
  const [loadingEntities, setLoadingEntities] = useState(false);

  const [xeroAccounts, setXeroAccounts] = useState([]);
  const [loadingXeroAccounts, setLoadingXeroAccounts] = useState(false);
  const [xeroAccountId, setXeroAccountId] = useState('');
  const [xeroNotConnected, setXeroNotConnected] = useState(false);

  const [internalAccounts, setInternalAccounts] = useState([]);
  const [loadingInternalAccounts, setLoadingInternalAccounts] = useState(false);
  const [internalAccountId, setInternalAccountId] = useState('');

  const [saving, setSaving] = useState(false);

  const loadEntities = useCallback(async () => {
    setLoadingEntities(true);
    try {
      const { data } = await xeroBankFeedAPI.listEntities();
      setEntities(data.entities || []);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoadingEntities(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      loadEntities();
      setEntityId('');
      setXeroAccounts([]);
      setXeroAccountId('');
      setXeroNotConnected(false);
      setInternalAccounts([]);
      setInternalAccountId('');
    }
  }, [open, loadEntities]);

  const loadXeroAccounts = useCallback(async (selectedEntityId) => {
    setLoadingXeroAccounts(true);
    setXeroAccounts([]);
    setXeroAccountId('');
    setXeroNotConnected(false);
    try {
      const { data } = await xeroBankFeedAPI.listXeroAccounts(selectedEntityId);
      setXeroAccounts(data.accounts || []);
    } catch (err) {
      if (isXeroNotConnectedError(err)) {
        setXeroNotConnected(true);
      } else {
        toast.error(getErrorMessage(err));
      }
    } finally {
      setLoadingXeroAccounts(false);
    }
  }, []);

  const loadInternalAccounts = useCallback(async (selectedEntityId) => {
    setLoadingInternalAccounts(true);
    setInternalAccounts([]);
    setInternalAccountId('');
    try {
      const { data } = await xeroBankFeedAPI.listInternalAccounts(selectedEntityId);
      setInternalAccounts(data.accounts || []);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoadingInternalAccounts(false);
    }
  }, []);

  const onEntityChange = (value) => {
    setEntityId(value);
    if (value) {
      loadXeroAccounts(value);
      loadInternalAccounts(value);
    }
  };

  const selectedInternalAccount = internalAccounts.find((a) => a.id === internalAccountId);

  const save = async () => {
    if (!entityId || !xeroAccountId || !internalAccountId) {
      toast.error('Select an entity, a Xero account, and an internal account.');
      return;
    }
    setSaving(true);
    try {
      const xeroAccount = xeroAccounts.find((a) => a.account_id === xeroAccountId);
      await xeroBankFeedAPI.createMapping({
        entity_id: entityId,
        xero_account_id: xeroAccountId,
        xero_account_name: xeroAccount?.name || null,
        internal_bank_account_id: internalAccountId,
      });
      toast.success('Mapping created.');
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Configure Xero bank-account mapping</DialogTitle>
          <DialogDescription>
            Maps a Xero bank account to an internal BR OS bank account for the
            daily read-only accounting-reconciliation pull. Display and
            ranking-boost only -- never auto-confirms a match. One active
            mapping per internal account is enforced.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <label className="text-xs font-medium block mb-1">Entity</label>
            <select
              data-testid="xero-bankfeed-configure-entity"
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-2 text-sm"
              value={entityId}
              onChange={(e) => onEntityChange(e.target.value)}
              disabled={loadingEntities}
            >
              <option value="">Select an entity...</option>
              {entities.map((e) => (
                <option key={e.entity_id} value={e.entity_id}>
                  {e.canonical_name}{!e.tenant_connected ? ' (no connected Xero tenant)' : ''}
                </option>
              ))}
            </select>
          </div>

          {entityId && (
            <div>
              <label className="text-xs font-medium block mb-1">Xero bank account</label>
              {loadingXeroAccounts ? (
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading Xero accounts...
                </div>
              ) : xeroNotConnected ? (
                <div
                  className="flex items-start gap-2 text-xs text-amber-600 dark:text-amber-400 border border-amber-500/30 rounded-md p-2.5"
                  data-testid="xero-bankfeed-configure-not-connected"
                >
                  <Unplug className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                  <span>
                    Connect Xero for this entity first.{' '}
                    <Link to="/integrations" className="underline font-medium">
                      Go to Integrations &rarr; Xero
                    </Link>
                  </span>
                </div>
              ) : (
                <select
                  data-testid="xero-bankfeed-configure-xero-account"
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-2 text-sm"
                  value={xeroAccountId}
                  onChange={(e) => setXeroAccountId(e.target.value)}
                >
                  <option value="">Select a Xero account...</option>
                  {xeroAccounts.map((a) => (
                    <option key={a.account_id} value={a.account_id}>
                      {a.name || '(unnamed)'} ({a.code || 'no code'}) {a.currency || ''}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}

          {entityId && !xeroNotConnected && (
            <div>
              <label className="text-xs font-medium block mb-1">Internal BR OS bank account</label>
              {loadingInternalAccounts ? (
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading internal accounts...
                </div>
              ) : (
                <select
                  data-testid="xero-bankfeed-configure-internal-account"
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-2 text-sm"
                  value={internalAccountId}
                  onChange={(e) => setInternalAccountId(e.target.value)}
                >
                  <option value="">Select an internal account...</option>
                  {internalAccounts.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.account_name || a.bank_name || a.id} ({a.currency})
                      {a.has_xero_mapping ? ' -- already has an active Xero mapping' : ''}
                    </option>
                  ))}
                </select>
              )}
              {selectedInternalAccount?.has_xero_mapping && (
                <p
                  className="text-xs text-amber-600 dark:text-amber-400 mt-1 flex items-center gap-1.5"
                  data-testid="xero-bankfeed-configure-conflict-warning"
                >
                  <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                  This account already has an active Xero mapping. Saving will
                  be blocked until it is deactivated.
                </p>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-between items-center pt-2">
          <Button variant="ghost" size="sm" onClick={loadEntities} disabled={loadingEntities}>
            <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
            Refresh entities
          </Button>
          <Button
            size="sm"
            onClick={save}
            disabled={saving || !entityId || !xeroAccountId || !internalAccountId}
            data-testid="xero-bankfeed-configure-save"
          >
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}
            Save mapping
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
